import axios from 'axios';
import Vue from 'vue';
import {generateUUID} from '@/utils/helpers';
import UAParser from 'ua-parser-js';
import store from '@/store/store';
import {LOCATIONS, URL_QUERIES} from '@/const';

const ANALYTICS_SESSION_HASH = 'visaicV3-session';
const DEVICE = 'desktop';
const SESSION_ENDPOINT = '/datalake/session/';
const THREE_HOURS = 1000 * 60 * 60 * 3;

/**
 * Generates a session-style id following a full UUID v4 + timestamp pattern,
 * e.g. `b3bfe864-fc23-45fd-b3a2-1c3a75a301ac-1778534678499`.
 * @return {string}
 */
function generateSessionId() {
  const timestamp = new Date().valueOf();
  return ('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx-' + timestamp).replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Class representing the VisaicV3Analytics.
 * Handles analytics tracking for the application.
 * @param {object} data - The data object containing the user and session information.
 * @return {VisaicV3Analytics} - The VisaicV3Analytics instance.
 */
class VisaicV3Analytics {
  #apiUrl = 'https://api-analytics.yarestream.com';
  #ttl = 1000 * 60 * 60 * 24;
  #profileHash = null;
  #userId = null;
  parser = new UAParser();
  #device = this.parser.getDevice().type || DEVICE;
  #apiKey = null;
  // Dedupes concurrent session bootstraps so multiple early events don't each
  // fire their own POST to /datalake/session/ while the first one is in flight.
  #sessionPromise = null;
  /**
   * Initializes the analytics with user and session information.
   * @param { string|number } userId
   * @param {string} profile
   * @param {string} apiKey
   */
  init({
    userId,
    profile,
    apiKey,
  }) {
    this.userId = userId;
    this.profileHash = profile;
    this.#apiKey = apiKey;

    if (!this.sessionHash) {
      this.#ensureSession().catch(() => {});
    }
  }

  /**
   * @param {string|number|null} id
   */
  set userId(id) {
    this.#userId = id;
  }

  /**
   * @param {string} hash
   */
  set sessionHash(hash) {
    hash && Vue.$storage.set(ANALYTICS_SESSION_HASH, hash, {ttl: this.#ttl});
  }

  /**
   * @param {string} hash
   */
  set profileHash(hash) {
    this.#profileHash = hash;
  }

  /**
   * @return {string | null}
   */
  get sessionHash() {
    const hash = Vue.$storage.get(ANALYTICS_SESSION_HASH);
    if (hash instanceof Object && hash.ttl) {
      Vue.$storage.remove(ANALYTICS_SESSION_HASH);
      return null;
    }

    return Vue.$storage.get(ANALYTICS_SESSION_HASH);
  }

  /**
   * @return {string}
   */
  get profileHash() {
    return this.#profileHash;
  }

  /**
   * Getter for the outerTracker property.
   * It retrieves the value of the outerTracker from the Vue's storage.
   * @return {Object|null} - The outerTracker object if it exists in the Vue's storage,
   * otherwise null.
   */
  get outerTracker() {
    return Vue.$storage.get(URL_QUERIES.V3_OUTER_TRACKING);
  }

  /**
   * Setter for the outerTracker property.
   * It sets the value of the outerTracker in the Vue's storage with a time to live (ttl)
   * of three hours.
   * @param {Object|null} value - The value to set for the outerTracker.
   */
  set outerTracker(value) {
    Vue.$storage.set(URL_QUERIES.V3_OUTER_TRACKING, value, {ttl: THREE_HOURS});
  }

  /**
   * Generates a session id locally (if one isn't already persisted) and
   * registers it with the backend via /datalake/session/. We no longer call
   * /datalake/hash/ - the backend just receives the id we send up.
   * @return {Promise<string>}
   */
  async createSession() {
    if (!this.sessionHash) {
      this.sessionHash = generateSessionId();
    }

    await this.#request({
      url: SESSION_ENDPOINT,
      data: {
        profile: this.profileHash,
        session: this.sessionHash,
        device_type: this.#device,
        user_agent: encodeURIComponent(this.parser.getUA()),
        resolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
        platform: 'web',
      },
    }).catch(() => {});

    return this.sessionHash;
  }

  /**
   * @param {string} event
   * @param {any} req
   */
  event({
    event,
    ...req
  }) {
    this.#request({
      url: '/datalake/event/',
      data: {
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        user_id: this.#userId,
        user_email: store?.state?.auth?.user?.email,
        event,
        ...req,
      },
    }).catch(()=>{});
  }


  /**
   * @param {string} email
   */
  loginEvent(email) {
    this.event({
      event: 'login',
      timestamp: Math.floor(Date.now() / 1000),
      email: email,
      user_id: store?.state?.auth?.user?.id,
      user_email: store?.state?.auth?.user?.email,
    });
  }

  /**
   * @param {string|null} uuid
   * @param {string|number} streamId
   * @param {string} type
   * @param {string} event
   * @param {object} extraData
   * @return {string}
   */
  videoEvent({
    uuid,
    streamId,
    type,
    event = 'first_frame',
    extraData= {},
  }) {
    const UUID = uuid || generateUUID();
    this.#request({
      url: '/datalake/video/event/',
      data: {
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        video_view_uuid: UUID,
        stream_id: streamId,
        stream_type: type,
        event,
        user_id: this.#userId,
        user_email: store?.state?.auth?.user?.email,
        ...extraData,
      },
    }).catch(()=>{});

    return UUID;
  }

  /**
   * @param {string} uuid
   * @param {string|number} streamId
   * @param {string} type
   * @param {number} time
   * @param {string|undefined} category
   * @param {string|undefined} sport
   * @param {string|undefined} gender
   * @param {string|undefined} school
   * @param {string|undefined} league
   */
  videoViewingTime({
    uuid,
    streamId,
    type,
    time,
    category,
    sport,
    gender,
    school,
    league,
  }) {
    this.#request({
      url: '/datalake/video/viewing/',
      data: {
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        user_id: this.#userId,
        user_email: store?.state?.auth?.user?.email,
        video_view_uuid: uuid,
        stream_id: streamId,
        stream_type: type,
        viewing_time: time,
        category,
        sport,
        gender,
        school,
        league,
      },
    }).catch(()=>{});
  };

  /**
   * Send a video event for a typed video (e.g., LIBRARY, CLIP).
   * Uses the /datalake/video_type/event/ endpoint and identifies the
   * record with `video_id` + `video_type` (instead of stream_id/stream_type).
   * The session is reused (same session_uuid as the existing flow).
   * @param {string|null} uuid - video_view_uuid; generated when missing.
   * @param {string|number} videoId
   * @param {string} videoType - e.g., 'LIBRARY', 'CLIP'.
   * @param {string} event - e.g., 'ready', 'first_frame', 'complete', 'error'.
   * @param {object} extraData
   * @return {string}
   */
  videoTypeEvent({
    uuid,
    videoId,
    videoType,
    event = 'first_frame',
    extraData = {},
  }) {
    const UUID = uuid || generateUUID();
    this.#request({
      url: '/datalake/video_type/event/',
      data: {
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        video_view_uuid: UUID,
        video_id: videoId,
        video_type: videoType,
        event,
        user_id: this.#userId,
        user_email: store?.state?.auth?.user?.email,
        ...extraData,
      },
    }).catch(()=>{});

    return UUID;
  }

  /**
   * Send total viewing time for a typed video (e.g., LIBRARY, CLIP).
   * Uses the /datalake/video_type/viewing/ endpoint.
   * @param {string} uuid
   * @param {string|number} videoId
   * @param {string} videoType
   * @param {number} time - viewing time in seconds.
   * @param {object} extraData
   */
  videoTypeViewingTime({
    uuid,
    videoId,
    videoType,
    time,
    extraData = {},
  }) {
    this.#request({
      url: '/datalake/video_type/viewing/',
      data: {
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        user_id: this.#userId,
        user_email: store?.state?.auth?.user?.email,
        video_view_uuid: uuid,
        video_id: videoId,
        video_type: videoType,
        viewing_time: time,
        ...extraData,
      },
    }).catch(()=>{});
  };
  #rollbackRequestBodyToObject = (data) => {
    let preparedData = data;
    if (data instanceof FormData) {
      const obj = {};
      for (const [key, value] of data.entries()) {
        const name = key.replace('[]', '');

        if (key.includes('[]')) {
          obj[name] = obj[name] || [];
          obj[name].push(value);
        } else {
          obj[name] = value;
        }
      }
      preparedData = obj;
    } else if (typeof data === 'string') {
      try {
        preparedData = JSON.parse(data);
      } catch {
        preparedData = {};
      }
    }

    return Object.fromEntries(Object.entries(preparedData).map(([key, val]) => {
      const formattedValue = val instanceof Object ? JSON.stringify(val) : val;
      return [key, formattedValue];
    }));
  };

  /**
   * Tracks an API request event and logs it using the `event` method.
   *
   * @param {Object} responseObject - The response object from the API request.
   * @param {string} subtitle - A subtitle for the event.
   */
  trackRequest = (responseObject, subtitle) => {
    try {
      const configMeta = responseObject.config?.metadata || {};
      const dataLength = responseObject.data?.data?.length;
      const total = responseObject.data?.meta?.total;
      const error = responseObject.response?.data?.message;
      const status = responseObject.status;
      const url = responseObject.config?.url;
      const requestBody = this.#rollbackRequestBodyToObject(responseObject.config.data);

      this.event({
        event: 'api_request',
        sub_event: subtitle,
        profile: this.profileHash,
        session_uuid: this.sessionHash,
        user_id: this.#userId,
        response_length: dataLength,
        total_length: total,
        request_url: url,
        error,
        status,
        ...configMeta,
        ...requestBody,
      });
    } catch (e) {
      console.log('api_request event error', e);
    }
  };

  /**
   * Tracking home page visiting for logged and unlogged users
   * @param {string} page
   * @param {boolean} logged
   */
  pageVisit({
    page,
  }) {
    this.event({
      event: 'visit_page',
      api_key: this.#apiKey,
      page,
      authorized: String(Number(Boolean(this.#userId))),
      timestamp: Math.floor(Date.now() / 1000),
      outer_referrer: String(document.referrer) || '-',
      from_page: Vue.$storage.get('previous_page_for_analytics') || '-',
    });
  }

  /**
   * Ensures a valid session_uuid exists, creating one when missing.
   * Concurrent callers share a single in-flight bootstrap.
   * @return {Promise<void>}
   */
  async #ensureSession() {
    if (this.sessionHash) return;

    if (!this.#sessionPromise) {
      this.#sessionPromise = this.createSession().finally(() => {
        this.#sessionPromise = null;
      });
    }

    await this.#sessionPromise;
  }

  /**
   * send request
   * @param {string} url
   * @param {string} method
   * @param {object} data
   * @return {Promise}
   */
  async #request({
    url,
    method = 'get',
    data,
  }) {
    if (data) {
      data.origin_url = location.href;
    }

    if (!this.profileHash) {
      return Promise.reject(new Error('No profile key for analytics'));
    }

    // For every endpoint other than the session bootstrap itself, guarantee
    // a live session_uuid. We re-read `this.sessionHash` after attempting
    // creation so we never send a stale `null` that was snapshotted into
    // `data` by the caller (root cause of "Missing 'session_uuid' parameter"
    // backend errors).
    if (url !== SESSION_ENDPOINT) {
      await this.#ensureSession();
      if (!this.sessionHash) {
        return Promise.reject(new Error('this request must have valid session_uuid'));
      }
      if (data) {
        data.session_uuid = this.sessionHash;
      }
    }

    if (this.outerTracker && data) {
      data[URL_QUERIES.V3_OUTER_TRACKING] = this.outerTracker;
    }
    const parameterizedUrl = new URL(url, this.#apiUrl);

    if (data && method === 'get') {
      const search = parameterizedUrl.searchParams;
      Object.entries(data).forEach(([key, val]) => val && search.set(key, val));
    }

    // eslint-disable-next-line max-len
    // console.info('DEBUG track V3 SENT REQUEST:', {url: parameterizedUrl.href, data: data, method: method}, location.hostname, LOCATIONS.LOCALHOST);
    if (location.hostname === LOCATIONS.LOCALHOST ) {
      // eslint-disable-next-line max-len
      // console.info('track V3 SENT REQUEST:', {url: parameterizedUrl.href, data: data, method: method});
      return Promise.reject(new Error('no request from localhost'));
    }

    return axios({
      url: parameterizedUrl.href,
      method: method,
      timeout: 30000,
      data,
    });
  }
}

const analytics = new VisaicV3Analytics();
export default analytics;
